// A screen's own title and buttons in the app's top bar (the parent and staff apps): the shell
// leaves a title spot and a buttons spot in its bar, and a screen fills them with <ShellHeader>
// (a conversation shows its name and its options there). Where no shell offers the spots (the
// console), useShellSlotsHere() is null and the screen keeps its own header.
import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface Slots { title: HTMLElement | null; actions: HTMLElement | null }
const SlotsContext = createContext<Slots | null>(null);
export const ShellSlotsProvider = SlotsContext.Provider;

/**
 * For an app shell (call it before any early return): `title(pageTitle)` and `actions` go in its
 * bar, `value` to ShellSlotsProvider around the screens.
 */
export function useShellSlots() {
  const [title, setTitle] = useState<HTMLElement | null>(null);
  const [actions, setActions] = useState<HTMLElement | null>(null);
  return {
    title: (pageTitle: ReactNode) => <span className="pmu-shellslot" ref={setTitle}><span className="pmu-shellslot__fallback">{pageTitle}</span></span>,
    actions: <span className="pmu-shellslot-actions" ref={setActions} />,
    value: { title, actions } as Slots,
  };
}

/** The shell's spots, or null where the screen is not inside an app shell that has them. */
export function useShellSlotsHere(): Slots | null {
  const s = useContext(SlotsContext);
  return s?.title ? s : null;
}

/** Puts a title (and a second line) and buttons in the app's top bar while the screen is shown. */
export function ShellHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  const s = useShellSlotsHere();
  if (!s) return null;
  return (
    <>
      {createPortal(<span className="pmu-shellslot__own"><span className="pmu-shellslot__title">{title}</span>{subtitle && <span className="pmu-shellslot__sub">{subtitle}</span>}</span>, s.title!)}
      {actions && s.actions && createPortal(actions, s.actions)}
    </>
  );
}
